"use client";
import Image from "next/image";

import { streamChatAPI } from "@/utils/chatStream";
import { sendCustomGaEvent } from "@/scripts/ga";
import { useAuth } from "@/hook/useAuth";
import AnonymousFollowUpModal from "@/components/common/AnonymousFollowUpModal";
import AnonymousRouteLeadModal from "@/components/common/AnonymousRouteLeadModal";
import {
  addMessage,
  clearMessages,
  Message,
  setEmbeddingQuery,
  setLoading,
  setPreferencesFormSubmitted,
  setShowDynamicForm,
  setDynamicFields,
  setActiveFlow,
  setFlowStep,
  updateLastMessage,
  addOrUpdateAssistantMessage,
  addOrUpdateAssistantMessageComplete,
} from "@/store/feature";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  DynamicFieldOptions,
  SearchResult,
  CtaData,
} from "@/store/types/aichat";
import { Calendar, Sparkles, X, ArrowDown } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { chatBotSuggestions } from "../common/Helper";
import {
  GradientStar,
  PurpleGradientDelete,
  User,
  WhiteRocket,
} from "../common/Icons";
import TalkToExperts from "../homepage/TalkToExperts";
import ChatFollowUpQuestions, {
  normalizeFollowUpQuestions,
} from "./ChatFollowUpQuestions";
import ServerDrivenForm from "./ServerDrivenForm";

const FullScreenInterface = () => {
  const isInitialized = useRef(false);
  const hasAutoOpenedRouteLead = useRef(false);
  const [inputMessage, setInputMessage] = useState("");
  const [expertForm, setExpertForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [dynamicFormOptions, setDynamicFormOptions] =
    useState<DynamicFieldOptions>({});
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showRouteLeadModal, setShowRouteLeadModal] = useState(false);
  const [followUpInitialTab, setFollowUpInitialTab] = useState<
    "contact" | "signup"
  >("contact");
  const [hasCourseResultsGate, setHasCourseResultsGate] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const baseTextRef = useRef<string>(""); // text in input before voice started
  // Drawer state for mobile
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isFollowUpMessageClosed, setIsFollowUpMessageClosed] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const userScrolledUp = useRef(false);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<Message[]>([]);
  const { isAnonymous, user, loading } = useAuth();
  const {
    messages,
    isLoading,
    showDynamicForm,
    dynamicFields,
    preferencesFormSubmitted,
    activeFlow,
    flowStep,
  } = useAppSelector((state) => state.chat);
  const dispatch = useAppDispatch();

  const userMessageCount = messages.filter((msg) => msg.type === "user").length;
  const askForFollowUp = userMessageCount >= 5;
  const FREE_MESSAGE_LIMIT = 20;
  const hasExceededLimit =
    isAnonymous && userMessageCount >= FREE_MESSAGE_LIMIT;
  const shouldGateAnonymous =
    isAnonymous && (hasExceededLimit || hasCourseResultsGate);
  const botMessages = messages.filter((msg) => msg.type === "assistant");
  const showFormButton = botMessages.length >= 1 && !preferencesFormSubmitted;
  const latestAssistantMessage = [...messages]
    .reverse()
    .find((msg) => msg.type === "assistant");
  const canShowFollowUpQuestions =
    !isLoading && !showDynamicForm && !shouldGateAnonymous;

  // Keep messagesRef in sync so handleSendMessage can read current messages
  // without needing `messages` in its useCallback dependency array.
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 76)}px`;
    }
  }, [inputMessage]);

  /* ── Voice-to-Text (continuous + interim results) ───────────────────────── */
  const autoSendTimeoutRef = useRef<any>(null);

  // Initialise recognition once on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition: SpeechRecognition = new SpeechRecognition();
    recognition.continuous = true;       // keep listening until stopped
    recognition.interimResults = true;   // fire results while speaking
    recognition.lang = "en-US";

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // Build transcript from all results (interim + final)
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join("");
      // Prepend whatever the user had already typed before starting voice
      const fullText = baseTextRef.current
        ? baseTextRef.current + " " + transcript
        : transcript;

      setInputMessage(fullText);

      // Silence auto-send timer: auto-sends if no new speech is detected for 1.8 seconds
      if (autoSendTimeoutRef.current) {
        clearTimeout(autoSendTimeoutRef.current);
      }
      autoSendTimeoutRef.current = setTimeout(() => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
        if (fullText.trim() && !shouldGateAnonymous) {
          handleSendMessage(fullText);
          setInputMessage("");
        }
      }, 1800);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = (e) => {
      console.error("Speech recognition error:", e);
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldGateAnonymous]);

  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) {
      alert("Voice input is not supported in your browser.");
      return;
    }
    if (autoSendTimeoutRef.current) {
      clearTimeout(autoSendTimeoutRef.current);
      autoSendTimeoutRef.current = null;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      // Snapshot existing text so we can prepend it to the live transcript
      baseTextRef.current = inputMessage;
      recognitionRef.current.start();
      setIsListening(true);
      inputRef.current?.focus();
    }
  }, [isListening, inputMessage]);

  const expertFromHandler = () => {
    void sendCustomGaEvent("chatbot_talk_to_expert_click", {
      source: "fullscreen_interface",
      is_anonymous: isAnonymous,
    });
    setExpertForm(true);
    document.body.classList.add("overflow-hidden");
  };

  const openFollowUpModal = (tab: "contact" | "signup" = "contact") => {
    void sendCustomGaEvent("chatbot_followup_modal_open", {
      source: "fullscreen_interface",
      tab,
      has_exceeded_limit: hasExceededLimit,
      has_course_results_gate: hasCourseResultsGate,
    });
    setFollowUpInitialTab(tab);
    setShowFollowUpModal(true);
  };

  // Add bot message with search results
  const addBotMessage = useCallback((
    content: string,
    searchResults?: SearchResult[],
    ctas?: Record<string, CtaData>,
    followUpQuestions?: string[],
  ) => {
    const botMessage: Message = {
      id: Date.now(),
      type: "assistant",
      content,
      searchResults,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      ctas,
      followUpQuestions,
    };

    dispatch(addMessage(botMessage));
  }, [dispatch]);

  // Add user message
  const addUserMessage = useCallback((content: string) => {
    const newMessage: Message = {
      id: Date.now(),
      type: "user",
      content,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    dispatch(addMessage(newMessage));
  }, [dispatch]);

  const handleServerFormSubmit = async (data: Record<string, string>) => {
    dispatch(setShowDynamicForm(false));
    dispatch(setDynamicFields([]));
    setDynamicFormOptions({});

    const formatDisplayValue = (value: string) => {
      const normalized = value.replace(/^europe\s*-\s*/i, "");
      return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    // Construct a readable conversational prompt using the user's answers
    const prompt = Object.entries(data)
      .map(([key, value]) => {
        // Clean up keys like 'overall_score' -> 'Overall Score'
        const cleanKey = key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
        return `${cleanKey}: ${formatDisplayValue(value)}`;
      })
      .join(", ");

    // Send the form data as preferences/context
    // Make the prompt visible in the conversation UI
    await handleSendMessage(prompt, data, false);
  };

  const handleServerFormCancel = () => {
    dispatch(setShowDynamicForm(false));
    dispatch(setDynamicFields([]));
    setDynamicFormOptions({});
  };

  const { userDetails } = useAppSelector((state) => state.userProfile);

  // Handle sending message — uses messagesRef to avoid stale closure over
  // the `messages` Redux state, which would otherwise require `messages` in
  // the dependency array and recreate this callback on every new message.
  const handleSendMessage = useCallback(async (
    message: string,
    formPreferences?: Record<string, string>,
    isHidden: boolean = false,
  ) => {
    if (!message.trim() && !formPreferences) return;

    if (!isHidden && message.trim()) {
      addUserMessage(message);
      void sendCustomGaEvent("chatbot_message_sent", {
        source: "fullscreen_interface",
        is_anonymous: isAnonymous,
      });
    }

    // Auto-scroll to bottom on new user message
    userScrolledUp.current = false;
    setShowScrollDown(false);
    setTimeout(() => scrollToBottom(), 100);

    dispatch(setLoading(true));

    const email = !isAnonymous ? (userDetails?.email || user?.email || null) : null;
    const phoneNumber = !isAnonymous
      ? (userDetails?.primary_phone_number || user?.phoneNumber || null)
      : null;
    const userId = !isAnonymous
      ? (userDetails?.user_id || userDetails?.id || user?.uid || null)
      : null;

    try {
      // Read messages from ref to get the latest state without
      // needing `messages` in the useCallback deps.
      const currentMessages = messagesRef.current;

      await streamChatAPI({
        userMessage: message,
        messages: currentMessages.map((msg) => ({
          type: msg.type as "user" | "assistant",
          content: msg.content,
          timestamp: msg.timestamp,
        })),
        isAnonymous,
        userInfo: {
          email,
          phoneNumber,
          userId,
        },
        eligibilityData: formPreferences,
        activeFlow,
        flowStep,
        onDelta: (delta) => {
          dispatch(setLoading(false));
          dispatch(addOrUpdateAssistantMessage(delta));
        },
        onComplete: (apiResponse) => {
          dispatch(setLoading(false));

          // Save the returned flow state to Redux if present
          if (apiResponse?.flow_state) {
            if (apiResponse.flow_state.active_flow !== undefined) {
              dispatch(setActiveFlow(apiResponse.flow_state.active_flow));
            }
            if (apiResponse.flow_state.step !== undefined) {
              dispatch(setFlowStep(apiResponse.flow_state.step));
            }
          }

          if (apiResponse?.ui_type === "form" && apiResponse.fields) {
            dispatch(setDynamicFields(apiResponse.fields));
            setDynamicFormOptions(apiResponse.dynamic_options || {});
            dispatch(setShowDynamicForm(true));
            // Update/Add the last message with form intro message
            dispatch(addOrUpdateAssistantMessageComplete({
              content: apiResponse.message || "",
              ctas: apiResponse.ctas,
              followUpQuestions: normalizeFollowUpQuestions(apiResponse.follow_up_questions),
            }));
          } else if (apiResponse?.message) {
            dispatch(addOrUpdateAssistantMessageComplete({
              content: apiResponse.message,
              searchResults: apiResponse.search_results,
              ctas: apiResponse.ctas,
              followUpQuestions: normalizeFollowUpQuestions(apiResponse.follow_up_questions),
            }));

            void sendCustomGaEvent("chatbot_response_received", {
              source: "fullscreen_interface",
              has_search_results: (apiResponse?.search_results?.length || 0) > 0,
              is_anonymous: isAnonymous,
            });
          }

          // Store embedding query if provided
          if (apiResponse?.embeddingsquery) {
            dispatch(setEmbeddingQuery(apiResponse.embeddingsquery));
          }

          if (isAnonymous && (apiResponse?.search_results?.length || 0) > 0) {
            setHasCourseResultsGate(true);
            void sendCustomGaEvent("chatbot_anonymous_gate_triggered", {
              source: "fullscreen_interface",
              reason: "course_results",
            });
          }
        },
        onError: (errorMsg) => {
          dispatch(setLoading(false));
          dispatch(addOrUpdateAssistantMessageComplete({
            content: errorMsg || "I apologize, but I'm having trouble processing your request right now. Please try again later.",
          }));
        },
      });
    } catch (error) {
      console.error("Error sending message:", error);
      dispatch(setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserMessage, isAnonymous, user, userDetails, activeFlow, flowStep, dispatch]);

  //   ==================

  const handleFollowUpQuestionClick = (question: string) => {
    if (shouldGateAnonymous) {
      openFollowUpModal(hasExceededLimit ? "signup" : "contact");
      return;
    }

    if (isLoading || showDynamicForm) return;

    userScrolledUp.current = false;
    setShowScrollDown(false);

    setInputMessage("");
    void handleSendMessage(question);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !shouldGateAnonymous) {
      e.preventDefault();
      if (!inputMessage.trim() || isLoading || shouldGateAnonymous) return;
      handleSendMessage(inputMessage);
      setInputMessage("");
      inputRef.current?.focus();
    }
  };

  const handleClearChat = () => {
    void sendCustomGaEvent("chatbot_clear_chat_click", {
      source: "fullscreen_interface",
    });
    dispatch(clearMessages());
    isInitialized.current = false;
    hasAutoOpenedRouteLead.current = false;
    setShowRouteLeadModal(false);
    dispatch(setPreferencesFormSubmitted(false));
    dispatch(setShowDynamicForm(false));
    dispatch(setDynamicFields([]));
    setDynamicFormOptions({});
    setHasCourseResultsGate(false);
  };

  const EngagingLoadingIndicator: React.FC = () => {
    const [messageIndex, setMessageIndex] = useState(0);

    const LOADING_MESSAGES = [
      "Australia hosts highest ratio",
      "Global diversity enriches campus",
      "90% students land jobs",
      "STEM majors hired fastest",
      "MBAs double starting salaries",
      "Canada three year permit",
      "UK Graduate employment 80%",
      "UK Graduate Route: duration depends on current Home Office rules",
      "Germany: Europe's largest economy",
      "Global classrooms foster diversity",
      "Canada Tech talent in demand",
      "EU Graduate employment 87%",
      "Canada Graduate employment 89%",
    ];

    useEffect(() => {
      const interval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
      }, 2500);

      return () => clearInterval(interval);
    }, []);

    return (
      <div className="flex justify-start animate-fadeIn">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
            <Image
              src="/assets/images/png/chatBot.png"
              alt="Bot Avatar"
              width={30}
              height={30}
            />
          </div>
          <div className="px-4 py-3 text-sm relative rounded-2xl shadow-sm bg-white border border-gray-100 rounded-tl-none text-gray-800">
            <div className="flex items-center space-x-3">
              <div className="flex space-x-1 items-center h-2">
                <div className="w-1.5 h-1.5 bg-[#7C3AED] rounded-full animate-bounce [animation-duration:0.8s] [animation-delay:-0.3s]"></div>
                <div className="w-1.5 h-1.5 bg-[#7C3AED] rounded-full animate-bounce [animation-duration:0.8s] [animation-delay:-0.15s]"></div>
                <div className="w-1.5 h-1.5 bg-[#7C3AED] rounded-full animate-bounce [animation-duration:0.8s]"></div>
              </div>

              <div className="min-h-[20px] text-sm text-gray-700 font-medium ml-2">
                <span
                  key={messageIndex}
                  className="inline-block animate-fadeText"
                >
                  {LOADING_MESSAGES[messageIndex]}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      const isNearBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 150;
      userScrolledUp.current = !isNearBottom;
      setShowScrollDown(!isNearBottom);
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      userScrolledUp.current = false;
      setShowScrollDown(false);
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      const isNearBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 150;
      setShowScrollDown(!isNearBottom);
    }

    if (!shouldGateAnonymous && !showFormButton && !showDynamicForm) {
      inputRef.current?.focus();
    }
  }, [messages, shouldGateAnonymous, showFormButton, showDynamicForm]);

  useEffect(() => {
    setCurrentStep(isAnonymous ? 1 : 2);
  }, [expertForm, isAnonymous]);

  // Auto-expand drawer whenever messages arrive
  useEffect(() => {
    if (messages.length > 0) setDrawerOpen(true);
  }, [messages.length]);

  useEffect(() => {
    if (!isAnonymous) {
      setShowFollowUpModal(false);
      setShowRouteLeadModal(false);
      hasAutoOpenedRouteLead.current = false;
    }
  }, [isAnonymous]);

  useEffect(() => {
    void sendCustomGaEvent("chatbot_fullscreen_view", {
      is_anonymous: isAnonymous,
    });
  }, [isAnonymous]);

  useEffect(() => {
    if (
      loading ||
      !isAnonymous ||
      !askForFollowUp ||
      shouldGateAnonymous ||
      showFollowUpModal ||
      showRouteLeadModal ||
      hasAutoOpenedRouteLead.current
    ) {
      return;
    }

    hasAutoOpenedRouteLead.current = true;
    setShowRouteLeadModal(true);
  }, [
    askForFollowUp,
    loading,
    isAnonymous,
    shouldGateAnonymous,
    showFollowUpModal,
    showRouteLeadModal,
  ]);

  const ActionButtons: React.FC = () => {
    return (
      <div className="ff_poppins mt-6 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <span
            onClick={expertFromHandler}
            className="border-3 border-[#867bff] flex items-center justify-center space-x-2 px-4 py-3 rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105 font-medium text-sm cursor-pointer"
          >
            <Calendar className="w-4 h-4" />
            <span>Talk to Expert</span>
          </span>

          <Link
            href={"/ai-course-finder/eligibility?query=pick-country"}
            onClick={() => {
              void sendCustomGaEvent("chatbot_course_finder_click", {
                source: "fullscreen_interface",
                is_anonymous: isAnonymous,
              });
            }}
            className="flex items-center justify-center space-x-2 bg-[#867bff] text-white px-4 py-3 rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105 font-medium text-sm"
          >
            <Sparkles className="w-4 h-4" />
            <span>Course Finder</span>
          </Link>
        </div>
      </div>
    );
  };

  const ResultsFollowUpMessage: React.FC = () => {
    if (!askForFollowUp) return null;
    if (isAnonymous) return null;
    if (isFollowUpMessageClosed) return null;

    return (
      <div className="relative flex flex-col items-center justify-center text-center py-8 bg-gray-50 rounded-2xl border border-gray-100 my-4 shadow-sm mx-auto max-w-[95%]">
        <button
          onClick={() => setIsFollowUpMessageClosed(true)}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full p-1 shadow-sm border border-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start max-w-[85%]">
          <div className="flex-1">
            <h4 className="font-semibold mb-2">
              Want to see where you qualify? Check your eligibility now or book
              a session for personal support.
            </h4>
            <ActionButtons />
          </div>
        </div>
      </div>
    );
  };


  /* ─────────────────────────────────────────────────────────────────────────
   * renderInputBar() — plain function (NOT a React sub-component) so it never
   * unmounts/remounts on re-render → fixes the "can't type" bug.
   * Uses background-clip border trick so rounded-full works correctly.
   * ──────────────────────────────────────────────────────────────────────── */
  const renderInputBar = () => (
    <div className="relative w-full">
      {/*
        Outer wrapper container with 20px border radius
      */}
      <div
        style={{
          padding: "2px",
          borderRadius: "20px",
          background:
            "linear-gradient(91.9deg, #9D7AE8 0.34%, #6B3FC8 99.08%)",
          boxShadow: "0px 4px 12px 0px rgba(107, 63, 200, 0.15)",
        }}
      >
        <div
          className="flex items-center w-full bg-white gap-2 px-4 py-2.5"
          style={{ borderRadius: "18px" }}
        >
          {/* Textarea */}
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !shouldGateAnonymous) {
                e.preventDefault();
                if (!inputMessage.trim() || isLoading || shouldGateAnonymous) return;
                handleSendMessage(inputMessage);
                setInputMessage("");
                inputRef.current?.focus();
              }
            }}
            className="w-full placeholder:text-[#A0A0A0] ff_poppins text-base text-black focus:outline-none resize-none leading-relaxed bg-transparent overflow-y-auto overflow-x-hidden thin-scrollbar py-0.5"
            style={{ minHeight: "28px", maxHeight: "76px" }}
            placeholder={
              shouldGateAnonymous
                ? "Sign in to continue messaging..."
                : "Ask anything about studying abroad..."
            }
            rows={1}
            disabled={isLoading || shouldGateAnonymous || showDynamicForm}
          />

          {/* Right controls */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Clear chat */}
            {messages.length > 0 && (
              <button
                onClick={handleClearChat}
                className="cursor-pointer opacity-50 hover:opacity-100 transition-opacity p-1"
              >
                <PurpleGradientDelete color="#7470C8" />
              </button>
            )}

            {/* Mic Button (Rounded square with lavender border) */}
            <button
              type="button"
              onClick={toggleListening}
              disabled={shouldGateAnonymous || showDynamicForm}
              title={isListening ? "Stop listening" : "Voice input"}
              className={`w-10 h-10 flex items-center justify-center rounded-[12px] border transition-all duration-200 flex-shrink-0 ${isListening
                ? "bg-red-500 border-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.25)] animate-pulse"
                : "bg-white border-[#DDD6FE] hover:bg-[#F5F3FF]"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {isListening ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7C3AED"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              )}
            </button>

            {/* Send Button (Rounded square with solid lavender gradient) */}
            <button
              onClick={
                shouldGateAnonymous
                  ? () => openFollowUpModal(hasExceededLimit ? "signup" : "contact")
                  : () => {
                    handleSendMessage(inputMessage);
                    setInputMessage("");
                    inputRef.current?.focus();
                  }
              }
              disabled={
                (!inputMessage.trim() || isLoading) &&
                !shouldGateAnonymous &&
                !showDynamicForm
              }
              className="w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0 transition-transform duration-150 hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background:
                  "linear-gradient(135deg, #9D7AE8 0%, #6B3FC8 100%)",
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  /* ─────────────────────────────────────────────────────────────────────────
   * Shared Message List — used in both mobile drawer and desktop layout
   * ──────────────────────────────────────────────────────────────────────── */
  const MessageList = () => (
    <div className="flex-1 p-3 pb-4 space-y-4 relative max-w-[820px] mx-auto w-full">
      {messages.map((message) => {
        if (
          message.type === "assistant" &&
          !message.content?.trim() &&
          (!message.searchResults || message.searchResults.length === 0) &&
          (!message.ctas || Object.keys(message.ctas).length === 0)
        ) {
          return null;
        }

        const isFirstBotMessage =
          botMessages.length === 1 && message.type === "assistant";

        return (
          <div key={message.id}>
            {message.type === "user" ? (
              <div className="flex justify-end">
                <div className="relative">
                  <div
                    className="text-white shadow-sm text-sm min-w-[157px] max-w-[220px] md:min-w-0 md:max-w-[450px]"
                    style={{
                      background: "linear-gradient(135deg, #9D7AE8 0%, #6B3FC8 100%)",
                      width: "auto",
                      minHeight: "40px",
                      paddingTop: "10px",
                      paddingRight: "14px",
                      paddingBottom: "10px",
                      paddingLeft: "14px",
                      borderTopLeftRadius: "18px",
                      borderTopRightRadius: "18px",
                      borderBottomRightRadius: "4px",
                      borderBottomLeftRadius: "18px",
                      opacity: 1,
                    }}
                  >
                    <div className="markdown-content prose prose-sm max-w-none text-white">
                      <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                        {String(message.content)}
                      </ReactMarkdown>
                    </div>
                    <p className="text-[10px] mt-1 ff_poppins text-white/70">
                      {message.timestamp}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* Assistant bubble (same for both mobile & desktop) */
              <div className="flex justify-start">
                <div className="flex items-start space-x-3 max-w-[85%] min-w-0">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    <Image
                      src="/assets/images/png/chatBot.png"
                      alt="Bot Avatar"
                      width={30}
                      height={30}
                    />
                  </div>

                  <div className="relative min-w-0 flex-1">
                    <div className="px-4 pt-2 pb-2 text-sm relative rounded-2xl shadow-sm bg-white border border-gray-100 rounded-tl-none text-gray-800 overflow-hidden">
                      <div className="markdown-content prose prose-sm max-w-none overflow-x-auto">
                        <ReactMarkdown
                          rehypePlugins={[rehypeRaw]}
                          remarkPlugins={[remarkGfm]}
                          components={{
                            span: ({ node, children, ...props }: any) => {
                              const ctaId = props["data-cta"];
                              if (ctaId && message.ctas?.[ctaId]) {
                                const cta = message.ctas[ctaId];
                                if (isAnonymous) {
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => openFollowUpModal(hasExceededLimit ? "signup" : "contact")}
                                      className="text-[#7C3AED] underline font-medium cursor-pointer bg-transparent border-none p-0 inline"
                                    >
                                      {cta.label}
                                    </button>
                                  );
                                }
                                return <span className="text-[#7C3AED] font-medium">{cta.label}</span>;
                              }
                              return <span {...props}>{children}</span>;
                            },
                            a: ({ children, href, ...props }: any) => {
                              const flattenText = (node: any): string => {
                                if (typeof node === "string") return node;
                                if (typeof node === "number") return String(node);
                                if (Array.isArray(node)) return node.map(flattenText).join(" ");
                                if (node?.props?.children) return flattenText(node.props.children);
                                return "";
                              };

                              const text = flattenText(children).toLowerCase();
                              const shouldOpenFollowUp =
                                isAnonymous &&
                                (text.includes("click here") || href === "#" || href === "javascript:void(0)");

                              if (shouldOpenFollowUp) {
                                return (
                                  <button
                                    type="button"
                                    onClick={() => openFollowUpModal(hasExceededLimit ? "signup" : "contact")}
                                    className="text-white underline font-medium cursor-pointer bg-transparent border-none p-0 inline"
                                  >
                                    {children}
                                  </button>
                                );
                              }

                              return (
                                <a
                                  {...props}
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium underline underline-offset-2 text-[#7C3AED] hover:text-[#5e57eb] transition-colors"
                                >
                                  {children}
                                </a>
                              );
                            },
                            table: ({ children }: any) => (
                              <div className="not-prose my-4 rounded-[12px] sm:rounded-[18px] overflow-hidden border border-[#E0D8FF] bg-white shadow-sm w-full">
                                <div className="overflow-x-auto w-full">
                                  <table className="w-full text-left border-collapse min-w-[320px] sm:min-w-[450px]">
                                    {children}
                                  </table>
                                </div>
                              </div>
                            ),
                            thead: ({ children }: any) => (
                              <thead className="bg-[#0A1629] [&>tr]:!bg-[#0A1629] text-white uppercase text-[10px] sm:text-xs font-semibold tracking-wider">
                                {children}
                              </thead>
                            ),
                            tbody: ({ children }: any) => (
                              <tbody className="divide-y divide-gray-100 text-xs sm:text-sm text-[#333E5C] ff_poppins font-medium">
                                {children}
                              </tbody>
                            ),
                            tr: ({ children }: any) => (
                              <tr className="even:bg-[#FBF9FF] odd:bg-white transition-colors hover:bg-gray-50/50">
                                {children}
                              </tr>
                            ),
                            th: ({ children }: any) => (
                              <th className="py-2 px-3 sm:py-3.5 sm:px-4 text-left font-semibold text-white whitespace-nowrap">
                                {children}
                              </th>
                            ),
                            td: ({ children }: any) => (
                              <td className="py-2 px-3 sm:py-3.5 sm:px-4 text-xs sm:text-sm border-t border-gray-100 first:font-semibold first:text-[#1C2233] [&:nth-child(2)]:text-[#525B73] last:font-bold last:text-[#0A1629]">
                                {children}
                              </td>
                            ),
                          }}
                        >
                          {(() => {
                            let content = String(message.content);
                            if (message.ctas) {
                              Object.keys(message.ctas).forEach((key) => {
                                content = content
                                  .replace(`{{${key}}}`, `<span data-cta="${key}"></span>`)
                                  .replace(`{${key}}`, `<span data-cta="${key}"></span>`);
                              });
                            }
                            return content;
                          })()}
                        </ReactMarkdown>
                      </div>
                      <p className="text-[10px] mt-1 ff_poppins text-gray-400">
                        {message.timestamp}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isFirstBotMessage && !preferencesFormSubmitted}
            {message.type === "assistant" &&
              message.id === latestAssistantMessage?.id &&
              canShowFollowUpQuestions &&
              (message.followUpQuestions?.length || 0) > 0 && (
                <div className="ml-11 mt-2 max-w-[560px]">
                  <ChatFollowUpQuestions
                    questions={message.followUpQuestions || []}
                    onSelect={handleFollowUpQuestionClick}
                    disabled={isLoading || showDynamicForm}
                  />
                </div>
              )}
          </div>
        );
      })}

      {/* INLINE SERVER DRIVEN FORM */}
      {showDynamicForm && (
        <div className="w-full max-w-xl mx-auto my-6 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-[0px_8px_32px_0px_#5F57EB25] border border-[#5F57EB]/10 overflow-hidden">
            <div className="bg-white rounded-2xl px-6 pt-6 pb-6">
              <ServerDrivenForm
                fields={dynamicFields}
                dynamicOptions={dynamicFormOptions}
                onSubmit={handleServerFormSubmit}
                onCancel={handleServerFormCancel}
                isSubmitting={isLoading}
              />
            </div>
          </div>
        </div>
      )}

      <ResultsFollowUpMessage />
      {shouldGateAnonymous && (
        <div className="w-full flex justify-center py-2">
          <button
            onClick={() =>
              openFollowUpModal(
                hasExceededLimit ? "signup" : "contact",
              )
            }
            className="bg-[#867bff] text-white font-semibold px-5 py-2.5 rounded-xl"
          >
            Unlock Full Access
          </button>
        </div>
      )}
      {isLoading && <EngagingLoadingIndicator />}
      <div ref={messagesEndRef} />
    </div>
  );

  const hasMessages = messages.length > 0;

  return (
    <>
      {/* ═══════════════════════════════════════════════════════════════════
          MOBILE LAYOUT  (hidden on md+)
          ═══════════════════════════════════════════════════════════════════ */}
      <div
        className="md:hidden relative min-h-dvh overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, #120538 8.49%, #1D0A45 54.15%, #2D0A6B 91.51%)",
        }}
      >
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(70.71% 70.71% at 50% 50%, rgba(107, 63, 200, 0.4) 0%, rgba(0, 0, 0, 0) 70%)",
          }}
        />

        {/* Inline CSS animation for explore pills click transition */}
        <style dangerouslySetInnerHTML={{
          __html: `
          @keyframes chatFadeInUp {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-chat-enter {
            animation: chatFadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        `}} />

        {!hasMessages ? (
          /* ── MOBILE HERO MODE ── */
          <div className="flex flex-col min-h-dvh">
            {/* Top section */}
            <div className="relative z-10 flex flex-col items-center justify-center pt-[calc(72px+20px)] px-6 pb-6 text-center">
              <Image
                className="w-[72px]"
                width={97}
                height={158}
                src="/assets/images/png/chatBot/chatBot.png"
                alt="Edvia Bot"
              />
              <h1 className="ff_poppins font-extrabold text-[26px] leading-tight text-white mt-5">
                ASK. EXPLORE.{" "}
                <span
                  style={{
                    background: "linear-gradient(90deg, #C084FC 0%, #A855F7 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  STUDY ABROAD.
                </span>
              </h1>
              <p className="ff_poppins text-sm text-white/70 mt-3 max-w-[280px]">
                Explore universities, compare courses, estimate costs, and plan
                your future with AI.
              </p>
            </div>

            {/* Bottom white card panel (fixed, not draggable/resizable) */}
            <div className="relative z-10 flex-1 bg-white rounded-t-[24px] shadow-[0_-8px_40px_rgba(107,63,200,0.20)] flex flex-col px-5 pt-4 pb-6">
              {/* Decorative Pill */}
              <div className="flex justify-center mb-2">
                <div className="w-10 h-1.5 rounded-full bg-[#E0D9FF]" />
              </div>

              {/* Explore Topics */}
              <p className="ff_poppins text-sm font-semibold tracking-[0.15em] text-[#9C7EEB] text-center uppercase mb-2">
                Explore Topics
              </p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {chatBotSuggestions.map((chat, index) => {
                  const Icon = chat.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => handleSendMessage(chat.label)}
                      className="group cursor-pointer ff_poppins text-[15px] font-medium text-[#2D1B69] bg-[#F5F0FF] border border-[#DDD6FE] rounded-full px-3 py-2.5 transition-all duration-200 flex items-center gap-2 hover:bg-[#EDE9FF] active:scale-[0.97] text-left"
                    >
                      <Icon size={17} className="shrink-0 text-[#7C3AED]" />
                      <span className="truncate">{chat.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Input bar */}
              <div className="mt-auto">
                {renderInputBar()}
                <p className="text-center ff_poppins text-[13px] text-[#5E57EB] mt-2">
                  Built on trusted university data to help you make confident decisions.
                </p>
                {shouldGateAnonymous && (
                  <p className="text-center ff_poppins text-xs mt-2 text-red-500">
                    Anonymous access is limited. Please use Quick Contact or Sign Up to continue.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* ── MOBILE CHAT MODE (Animate entry on pill click) ── */
          <div className="animate-chat-enter flex flex-col min-h-dvh">
            {/* Fixed Chat Area with Drawer Shape — fills everything below header */}
            <div
              className="fixed left-0 right-0 bottom-0 z-10 bg-white flex flex-col rounded-t-[30px] overflow-hidden shadow-[0_-8px_40px_rgba(107,63,200,0.15)]"
              style={{ top: "72px" }}
            >
              {/* Decorative Pill inside Chat Mode white panel */}
              <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
                <div className="w-10 h-1.5 rounded-full bg-[#E0D9FF]" />
              </div>

              {/* Scrollable message thread */}
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto scrollBarHidden pt-2 relative"
              >
                <MessageList />
              </div>

              {showScrollDown && (
                <div className="sticky bottom-4 left-0 right-0 flex justify-center z-20 pb-2 pointer-events-none">
                  <button
                    onClick={scrollToBottom}
                    className="pointer-events-auto bg-white border border-gray-200 text-gray-600 shadow-md rounded-full p-2.5 hover:bg-gray-50 hover:text-gray-900 transition-colors flex items-center justify-center"
                  >
                    <ArrowDown className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Pinned bottom input */}
              <div className="flex-shrink-0 bg-white border-t border-gray-100 py-3 px-4">
                {renderInputBar()}
                <p className="text-center ff_poppins text-[13px] text-[#5E57EB] mt-2">
                  Built on trusted university data to help you make confident decisions.
                </p>
                {shouldGateAnonymous && (
                  <p className="text-center ff_poppins text-xs mt-2 text-red-500">
                    Anonymous access is limited. Please use Quick Contact or Sign Up to continue.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT  (hidden on mobile, shown on md+)
          ═══════════════════════════════════════════════════════════════════ */}
      <div
        className="hidden md:flex md:flex-col relative min-h-dvh"
        style={{
          background:
            "linear-gradient(160deg, #120538 8.49%, #1D0A45 54.15%, #2D0A6B 91.51%)",
        }}
      >
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            background:
              "radial-gradient(70.71% 70.71% at 50% 50%, rgba(107, 63, 200, 0.4) 0%, rgba(0, 0, 0, 0) 70%)",
          }}
        />

        {messages.length <= 0 ? (
          /* ── HERO MODE ── */
          <>
            {/* Top hero section — dark bg */}
            <div className="relative z-10 flex flex-col items-center justify-center pt-[calc(80px+48px)] px-6 text-center pb-10">
              <Image
                className="max-w-[80px] xl:max-w-[100px] w-full drop-shadow-[0_0_24px_rgba(168,85,247,0.4)]"
                width={100}
                height={163}
                src="/assets/images/png/chatBot/chatBot.png"
                alt="Edvia Bot"
              />
              <h1 className="ff_poppins font-extrabold text-[32px] lg:text-[44px] xl:text-[52px] leading-tight text-white mt-6">
                ASK. EXPLORE.{" "}
                <span
                  style={{
                    background: "linear-gradient(90deg, #C084FC 0%, #A855F7 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  STUDY ABROAD.
                </span>
              </h1>
              <p className="ff_poppins text-sm lg:text-base text-white/70 mt-3 max-w-[520px]">
                Explore universities, compare courses, estimate costs, and plan
                your future with AI.
              </p>
            </div>

            {/* White card panel — Explore Topics + Input (matches mobile drawer style) */}
            <div className="relative z-10 flex-1 bg-white rounded-t-[24px] shadow-[0_-8px_40px_rgba(107,63,200,0.20)] flex flex-col px-6 pt-4 pb-6">
              {/* Drag pill (decorative) */}
              <div className="flex justify-center mb-3">
                <div className="w-10 h-1.5 rounded-full bg-[#E0D9FF]" />
              </div>

              {/* Explore Topics */}
              <p className="ff_poppins text-[15px] font-semibold tracking-[0.18em] text-[#9C7EEB] text-center uppercase mb-3">
                Explore Topics
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 max-w-[820px] mx-auto w-full mb-4">
                {chatBotSuggestions.map((chat, index) => {
                  const Icon = chat.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => handleSendMessage(chat.label)}
                      className="group cursor-pointer ff_poppins text-[17px] font-medium text-[#2D1B69] bg-[#F5F0FF] border border-[#DDD6FE] rounded-full px-5 py-2.5 transition-all duration-200 flex items-center gap-2 hover:bg-[#EDE9FF] active:scale-[0.97]"
                    >
                      <Icon size={20} className="shrink-0 text-[#7C3AED]" />
                      <span>{chat.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Input bar */}
              <div className="max-w-[820px] mx-auto w-full mt-auto">
                {renderInputBar()}
                <p className="text-center ff_poppins text-[13px] text-[#5E57EB] mt-3">
                  Built on trusted university data to help you make confident decisions.
                </p>
                {shouldGateAnonymous && (
                  <p className="text-center ff_poppins text-xs mt-2 text-red-500">
                    Anonymous access is limited. Please use Quick Contact or Sign Up to continue.
                  </p>
                )}
              </div>
            </div>
          </>
        ) : (
          /* ── CHAT MODE — fixed full-height panel ── */
          <>
            {/* Full-height chat panel with Drawer Shape — fills everything below navbar */}
            <div
              className="fixed left-0 right-0 bottom-0 z-10 bg-white flex flex-col rounded-t-[30px] overflow-hidden shadow-[0_-8px_40px_rgba(107,63,200,0.15)]"
              style={{ top: "72px" }}
            >
              {/* Decorative Pill inside Chat Mode white panel */}
              <div className="flex justify-center pt-4 pb-2 flex-shrink-0">
                <div className="w-10 h-1.5 rounded-full bg-[#E0D9FF]" />
              </div>

              {/* Scrollable messages */}
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto scrollBarHidden pt-2 relative"
              >
                <MessageList />
              </div>

              {showScrollDown && (
                <div className="sticky bottom-4 left-0 right-0 flex justify-center z-20 pb-2 pointer-events-none">
                  <button
                    onClick={scrollToBottom}
                    className="pointer-events-auto bg-white border border-gray-200 text-gray-600 shadow-md rounded-full p-2.5 hover:bg-gray-50 hover:text-gray-900 transition-colors flex items-center justify-center"
                  >
                    <ArrowDown className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Sticky bottom input */}
              <div className="flex-shrink-0 bg-white border-t border-gray-100 py-3 px-4">
                <div className="max-w-[820px] mx-auto">
                  {renderInputBar()}
                  <p className="text-center ff_poppins text-[13px] text-[#5E57EB] mt-2">
                    Built on trusted university data to help you make confident decisions.
                  </p>
                  {shouldGateAnonymous && (
                    <p className="text-center ff_poppins text-xs mt-2 text-red-500">
                      Anonymous access is limited. Please use Quick Contact or Sign Up to continue.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>


      {/* ═══════════════════════════════════════════════════════════════════
          SHARED MODALS
          ═══════════════════════════════════════════════════════════════════ */}
      <AnonymousFollowUpModal
        isOpen={showFollowUpModal}
        setIsOpen={setShowFollowUpModal}
        isAnonymous={isAnonymous}
        initialTab={followUpInitialTab}
        hasExceededLimit={hasExceededLimit}
        platform="chatbot"
        source={
          hasExceededLimit
            ? "fullscreen_message_limit_gate"
            : hasCourseResultsGate
              ? "fullscreen_course_results_gate"
              : "fullscreen_chat_follow_up"
        }
        data={{
          userMessageCount,
          totalMessages: messages.length,
          activeFlow: activeFlow || null,
          flowStep: flowStep || null,
          hasCourseResultsGate,
          gateReason: hasExceededLimit
            ? "message_limit"
            : hasCourseResultsGate
              ? "course_results"
              : "manual",
        }}
        onContactSuccess={expertFromHandler}
      />
      <AnonymousRouteLeadModal
        isOpen={showRouteLeadModal}
        setIsOpen={setShowRouteLeadModal}
        isAnonymous={!loading && isAnonymous}
        platform="chatbot"
        source="fullscreen_chat_5_message_route_lead"
        contactButtonText="Join Now"
        data={{
          triggerType: "fullscreen_chat_5_message_popup",
          userMessageCount,
          totalMessages: messages.length,
          activeFlow: activeFlow || null,
          flowStep: flowStep || null,
        }}
      />
      {expertForm && (
        <TalkToExperts
          expertForm={expertForm}
          setExpertForm={setExpertForm}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      )}
    </>
  );
};

export default FullScreenInterface;
